import {useState} from "react";
import {App as AntdApp, Empty, Popconfirm, Progress, Switch, Tag, Tooltip} from "antd";
import {Button, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetContentPlansQuery,
	useUpdateContentPlanMutation,
	useDeleteContentPlanMutation,
} from "~/reduxs/api/contentApiSlice";
import ContentPlanWizard from "./ContentPlanWizard";

const GOAL_LABELS = {traffic: 'Traffic', branding: 'Thương hiệu', conversion: 'Chuyển đổi'};
const REVIEW_LABELS = {review_all: 'Duyệt dàn ý + bài', review_article: 'Chỉ duyệt bài', full_auto: 'Tự động hoàn toàn'};

/**
 * Tab "Kế hoạch" (Đợt 2): danh sách kế hoạch bài viết hàng loạt của dự án — tiến độ,
 * tạm dừng/chạy tiếp, xoá — + wizard tạo kế hoạch từ Kho từ khoá. Bài của kế hoạch
 * hiện trong tab "Bài viết" (lọc theo kế hoạch).
 */
function ContentPlansTab({projectId, quotaCan}) {

	const {message} = AntdApp.useApp();
	const [openWizard, setOpenWizard] = useState(false);

	const {data, isLoading, refetch} = useGetContentPlansQuery(projectId);
	const [updatePlan] = useUpdateContentPlanMutation();
	const [deletePlan] = useDeleteContentPlanMutation();

	if (isLoading) return <Loading />;

	const plans = data?.plans || [];

	const togglePlan = async (plan, active) => {
		try {
			await updatePlan({projectId, planId: plan.id, status: active ? 'active' : 'paused'}).unwrap();
			message.success(active ? 'Kế hoạch tiếp tục chạy' : 'Đã tạm dừng kế hoạch');
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	const removePlan = async (plan) => {
		try {
			await deletePlan({projectId, planId: plan.id}).unwrap();
			message.success('Đã xoá kế hoạch — bài chưa chạy bị huỷ, bài đã viết giữ lại');
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	return (
		<div>
			<div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}}>
				<Button
					primary small
					leftIcon={<FontAwesomeIcon icon="fa-light fa-calendar-lines-pen" />}
					disabled={quotaCan === false}
					onClick={() => setOpenWizard(true)}
				>
					Tạo kế hoạch
				</Button>
			</div>

			{plans.length === 0 ? (
				<Empty description="Chưa có kế hoạch nào — tạo kế hoạch để AI gom cụm từ khoá và viết bài hàng loạt theo lịch." />
			) : (
				<div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12}}>
					{plans.map((plan) => {
						const pct = plan.total > 0 ? Math.round((plan.done / plan.total) * 100) : 0;
						return (
							<div key={plan.id} className="app-card" style={{padding: 16}}>
								<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8}}>
									<div>
										<div style={{fontWeight: 600}}>{plan.name}</div>
										<div style={{fontSize: 12.5, color: '#6b7280', marginTop: 2}}>
											<Tag>{GOAL_LABELS[plan.goal] || plan.goal}</Tag>
											{plan.per_week > 0 && <Tag>{plan.per_week} bài/tuần</Tag>}
											{plan.review_mode && <Tag color="blue">{REVIEW_LABELS[plan.review_mode]}</Tag>}
										</div>
									</div>
									<Tooltip title={plan.status === 'active' ? 'Đang chạy — gạt để tạm dừng' : 'Đang tạm dừng'}>
										<Switch size="small" checked={plan.status === 'active'} onChange={(v) => togglePlan(plan, v)} />
									</Tooltip>
								</div>

								<Progress percent={pct} size="small" style={{margin: '10px 0 4px'}} />
								<div style={{display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#6b7280'}}>
									<span>{plan.done}/{plan.total} bài hoàn thành{plan.failed > 0 ? ` · ${plan.failed} lỗi` : ''}</span>
									<Popconfirm
										title="Xoá kế hoạch? Bài chưa chạy sẽ bị huỷ (không hoàn quota), bài đã viết giữ lại."
										okText="Xoá" cancelText="Huỷ"
										onConfirm={() => removePlan(plan)}
									>
										<span style={{cursor: 'pointer', color: '#dc2626'}}>
											<FontAwesomeIcon icon="fa-light fa-trash-can" /> Xoá
										</span>
									</Popconfirm>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<ContentPlanWizard
				projectId={projectId}
				open={openWizard}
				onCancel={() => setOpenWizard(false)}
				onCreated={() => { setOpenWizard(false); refetch(); }}
			/>
		</div>
	);
}

export default ContentPlansTab;
